import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useLanguage } from "../App";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BookingModal from "../components/BookingModal";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Calendar, Clock, ArrowLeft, Tag, Share2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/articles/${slug}`);
        setArticle(res.data);
        // Update page meta tags
        document.title = res.data.meta_title || res.data.title;
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) metaDesc.setAttribute("content", res.data.meta_description || res.data.excerpt);
      } catch (err) {
        navigate("/blog");
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();

    return () => {
      document.title = "Dakhla Kite Planet | Kitesurfing School in Dakhla, Morocco - IKO Certified";
    };
  }, [slug, navigate]);

  const readingTime = (content) => {
    const words = content?.replace(/<[^>]*>/g, "").split(/\s+/).length || 0;
    return Math.max(1, Math.ceil(words / 200));
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      await navigator.share({ title: article.title, url });
    } else {
      navigator.clipboard.writeText(url);
      alert("Link copied!");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header onBookingClick={() => setBookingModalOpen(true)} />
        <div className="flex items-center justify-center py-40">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
        </div>
      </div>
    );
  }

  if (!article) return null;

  // Article structured data
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.meta_description || article.excerpt,
    "image": article.featured_image,
    "author": { "@type": "Organization", "name": "Dakhla Kite Planet" },
    "publisher": {
      "@type": "Organization",
      "name": "Dakhla Kite Planet",
      "logo": { "@type": "ImageObject", "url": "https://customer-assets.emergentagent.com/job_acc6b5c6-4c76-49ba-a1fd-498cb047a8ad/artifacts/wxjoscue_Logo_DKP.jpeg" },
    },
    "datePublished": article.published_at,
    "dateModified": article.updated_at,
    "mainEntityOfPage": { "@type": "WebPage", "@id": window.location.href },
  };

  return (
    <div className="min-h-screen bg-white">
      <Header onBookingClick={() => setBookingModalOpen(true)} />

      {/* Article Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />

      {/* Hero Image */}
      {article.featured_image && (
        <div className="pt-16">
          <div className="h-[50vh] relative">
            <img
              src={article.featured_image}
              alt={article.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <div className="max-w-4xl mx-auto">
                <Link
                  to="/blog"
                  className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm mb-4 transition-colors"
                  data-testid="back-to-blog"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Blog
                </Link>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight" data-testid="article-title">
                  {article.title}
                </h1>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Meta bar */}
        <div className="flex flex-wrap items-center gap-4 mb-8 pb-6 border-b border-gray-200">
          {article.category && (
            <Badge className="bg-sky-100 text-sky-700 capitalize">{article.category}</Badge>
          )}
          <span className="text-sm text-gray-500 flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            {article.published_at
              ? new Date(article.published_at).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })
              : "Draft"}
          </span>
          <span className="text-sm text-gray-500 flex items-center gap-1">
            <Clock className="h-4 w-4" />
            {readingTime(article.content)} min read
          </span>
          <button
            onClick={handleShare}
            className="ml-auto text-gray-400 hover:text-gray-600 transition-colors"
            data-testid="share-btn"
          >
            <Share2 className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div
          className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-h2:text-2xl prose-h2:font-bold prose-h2:mt-10 prose-h2:mb-4 prose-h3:text-xl prose-h3:font-semibold prose-h3:mt-8 prose-h3:mb-3 prose-p:text-gray-700 prose-p:leading-relaxed prose-a:text-sky-600 prose-a:font-medium hover:prose-a:text-sky-800 prose-li:text-gray-700 prose-strong:text-gray-900 prose-ul:my-4 prose-li:my-1"
          dangerouslySetInnerHTML={{ __html: article.content }}
          data-testid="article-content"
        />

        {/* Tags */}
        {article.tags?.length > 0 && (
          <div className="mt-12 pt-6 border-t border-gray-200">
            <div className="flex items-center gap-2 flex-wrap">
              <Tag className="h-4 w-4 text-gray-400" />
              {article.tags.map((tag) => (
                <Badge key={tag} variant="outline" className="text-xs text-gray-500">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 bg-gradient-to-r from-sky-50 to-cyan-50 rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-3">
            Ready to Start Your Kite Adventure?
          </h3>
          <p className="text-gray-600 mb-6 max-w-lg mx-auto">
            Book your kitesurfing lessons in Dakhla with IKO certified instructors.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button
              onClick={() => setBookingModalOpen(true)}
              className="bg-gradient-to-r from-sky-600 to-cyan-600 hover:from-sky-700 hover:to-cyan-700 text-white px-8"
              data-testid="article-book-btn"
            >
              Book Now
            </Button>
            <Link to="/lessons">
              <Button variant="outline" className="px-8">
                View Lessons
              </Button>
            </Link>
          </div>
        </div>
      </article>

      <Footer />
      <BookingModal isOpen={bookingModalOpen} onClose={() => setBookingModalOpen(false)} />
    </div>
  );
};

export default ArticlePage;
